import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import noimg from "../Images/noimg.jpg";

/* ---------- tiny inline icons ---------- */
const IconHeart = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const IconCamera = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconBed = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" />
    <path d="M2 16h20" />
    <path d="M6 10V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3" />
  </svg>
);

const IconUsers = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconLocation = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IconChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconChevronRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/* ---------- helpers ---------- */
const createSlug = (title = "resort") =>
  String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const getRatingLabel = (r) => {
  if (r >= 4.5) return "Excellent";
  if (r >= 4.0) return "Very Good";
  if (r >= 3.5) return "Good";
  if (r >= 3.0) return "Average";
  return "New";
};

/* =====================================================================
   CUSTOM MANUAL IMAGE SLIDER
   ===================================================================== */
const ManualImageSlider = ({ images, alt = "hotel" }) => {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const total = images.length;

  const goTo = (i) => {
    if (i < 0) setIndex(total - 1);
    else if (i >= total) setIndex(0);
    else setIndex(i);
  };
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  /* swipe handling */
  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const onTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const distance = touchStartX.current - touchEndX.current;
    const SWIPE_MIN = 45;
    if (distance > SWIPE_MIN) next();
    else if (distance < -SWIPE_MIN) prev();
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div
      className="rc-slider"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* track */}
      <div
        className="rc-slider-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {images.map((img, i) => (
          <div className="rc-slide" key={i}>
            <img
              src={img}
              alt={`${alt} ${i + 1}`}
              className="resortcard-main-img"
              loading={i === 0 ? "eager" : "lazy"}
              onError={(e) => { e.currentTarget.src = noimg; }}
              draggable={false}
            />
          </div>
        ))}
      </div>

      {total > 1 && (
        <>
          {/* prev / next */}
          <button
            type="button"
            className="rc-nav rc-nav-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
          >
            <IconChevronLeft />
          </button>
          <button
            type="button"
            className="rc-nav rc-nav-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
          >
            <IconChevronRight />
          </button>

          {/* dots */}
          <div className="rc-dots">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`rc-dot ${i === index ? "is-active" : ""}`}
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>

          {/* counter */}
          <span className="resortcard-photos-pill">
            <IconCamera />
            <span>{index + 1}/{total}</span>
          </span>
        </>
      )}
    </div>
  );
};

/* ===================================================================== */
const ResortCard = ({ resort }) => {
  const navigate = useNavigate();
  const [wishlisted, setWishlisted] = useState(false);

  /* ---------------- API fields ---------------- */
  const rooms = Array.isArray(resort?.rooms) ? resort.rooms : [];

  const sortedRooms = [...rooms].sort(
    (a, b) => (Number(a?.price_per_night) || 0) - (Number(b?.price_per_night) || 0)
  );
  const cheapestRoom = sortedRooms[0] || null;
  const price = Number(cheapestRoom?.price_per_night) || 0;

  const roomTypes = [...new Set(rooms.map((r) => r?.room_type).filter(Boolean))];
  const totalRooms = rooms.reduce((sum, r) => sum + (Number(r?.no_of_rooms) || 0), 0);

  const hasBreakfast = rooms.some((r) => Number(r?.breakfast) === 1);
  const hasLunch = rooms.some((r) => Number(r?.lunch) === 1);
  const hasDinner = rooms.some((r) => Number(r?.dinner) === 1);

  const capacities = rooms.map((r) => Number(r?.max_guests) || 0).filter((n) => n > 0);
  const minCap = capacities.length ? Math.min(...capacities) : 0;
  const maxCap = capacities.length ? Math.max(...capacities) : 0;

  const amenities = Array.isArray(resort?.amenities) ? resort.amenities : [];
  const rating = Number(resort?.average_rating) || 0;

  /* ---------------- images ---------------- */
  const featuredImages = Array.isArray(resort?.featured_images)
    ? resort.featured_images.filter(Boolean)
    : [];
  const galleryImages = Array.isArray(resort?.gallery_images)
    ? resort.gallery_images.filter(Boolean)
    : [];

  const allImages = Array.from(
    new Set(
      [...featuredImages, ...galleryImages, resort?.featured_image].filter(Boolean)
    )
  );
  const imagesToShow = allImages.length ? allImages : [noimg];

  const reviewCount = Array.isArray(resort?.reviews) ? resort.reviews.length : 0;
  const cleanDescription = DOMPurify.sanitize(resort?.description || "");

  const handleClick = () => {
    const slug = createSlug(resort?.slug || resort?.name || "resort");
    const id = resort?.id;
    const random = Math.floor(100 + Math.random() * 900);
    const encodedId = (id || 0) + random;
    navigate(`/hotel/${slug}-${encodedId}-${random}`);
  };

  return (
    <motion.div
      className="resortcard-container"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      {/* ============== IMAGE COLUMN ============== */}
      <div className="resortcard-image-col">
        <ManualImageSlider images={imagesToShow} alt={resort?.name || "hotel"} />
      </div>

      {/* ============== INFO COLUMN ============== */}
      <div className="resortcard-info-col">
        <div className="resortcard-title-row">
          <h2 className="resortcard-title">{resort?.name}</h2>
        </div>

        {resort?.location && (
          <p className="resortcard-location">
            <span className="resortcard-location-icon"><IconLocation /></span>
            <span className="resortcard-location-highlight">{resort.location}</span>
          </p>
        )}

        {cleanDescription && (
          <div
            className="resortcard-description"
            dangerouslySetInnerHTML={{ __html: cleanDescription }}
          />
        )}


        {amenities.length > 0 && (
          <ul className="resortcard-features">
            {amenities.slice(0, 4).map((a) => (
              <li key={a.id}>
                {a.icon ? (
                  <img
                    src={a.icon}
                    alt=""
                    className="resortcard-amenity-icon"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span className="icon icon-check"><IconCheck /></span>
                )}
                <span>{a.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ============== PRICE COLUMN ============== */}
      <div className="resortcard-price-col">
        <div className="resortcard-rating-row">
          <span className="resortcard-rating-label">{getRatingLabel(rating)}</span>
          {rating > 0 && (
            <span className="resortcard-rating-value">{rating.toFixed(1)}</span>
          )}
        </div>
        {reviewCount > 0 && (
          <p className="resortcard-rating-count">({reviewCount} Reviews)</p>
        )}

        <div className="resortcard-price-block">
          {price > 0 ? (
            <>
              <div className="resortcard-price-row">
                <span className="currency">₹</span>
                <span className="value">{price.toLocaleString("en-IN")}</span>
              </div>
              <p className="resortcard-pernight">Per Night</p>
              {cheapestRoom?.room_type && (
                <p className="resortcard-room-type">{cheapestRoom.room_type}</p>
              )}
            </>
          ) : (
            <p className="resortcard-price-empty">Price on request</p>
          )}
        </div>

        {totalRooms > 0 && (
          <p className="resortcard-roomcount">{totalRooms} rooms available</p>
        )}

        <motion.button
          className="resortcard-btn"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleClick}
        >
          View Details
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ResortCard;